import {isFeatureEnabled} from 'feature-flags';
import {match} from 'ts-pattern';

import {
  KeyboardEventHandler,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';

import {flatten, isNotNil} from 'ramda';

import {MenuItemProps} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';

import {assignSafely, buildArray, getSafely} from 'shared/web';

import {MenuItemWithPin} from '../utils/addPinsToMenuItems';
import {useMenuItemsState} from './MenuItemsStateProvider';

interface SidebarKeyboardControlContextType {
  setSidebarItemRef: (ref: HTMLElement | null, id: string) => void;
  setMenuItemRef: (ref: HTMLElement | null, id: string) => void;
  setCreateMenuItemRef: (ref: HTMLElement | null, id: string) => void;
  openMenu: () => void;
  closeMenu: () => void;
  openCreateMenu: () => void;
  closeCreateMenu: () => void;
}

const SidebarKeyboardControlContext = createContext<SidebarKeyboardControlContextType | undefined>(
  undefined
);

interface SidebarKeyboardControlProviderProps {
  children: (
    sidebarRef: RefObject<HTMLDivElement | null>,
    onKeyDown: KeyboardEventHandler<HTMLDivElement>
  ) => ReactNode;
  sidebarPinnedMenuItems: string[];
  menuItemsWithPins: MenuItemWithPin<MenuItemProps>[];
  createMenuItems: MenuItemProps[];
}

export const SidebarKeyboardControlProvider = ({
  children,
  sidebarPinnedMenuItems,
  menuItemsWithPins,
  createMenuItems,
}: SidebarKeyboardControlProviderProps) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sidebarLinkRefs = useRef<(HTMLElement | null)[]>([]);
  const menuRefs = useRef<(HTMLElement | null)[]>([]);
  const createMenuRefs = useRef<(HTMLElement | null)[]>([]);
  const [focusedSidebarItemIndex, setFocusedSidebarItemIndex] = useState<number>(0);
  const [focusedMenuItemIndex, setFocusedMenuItemIndex] = useState<number | null>(null);
  const [focusedCreateMenuItemIndex, setFocusedCreateMenuItemIndex] = useState<number | null>(null);
  const focusableSidebarItems = useMemo(
    () =>
      buildArray<string>([])
        .add('sidebar-menu')
        .add(...sidebarPinnedMenuItems)
        .when(
          createMenuItems.length > 0 &&
            !isFeatureEnabled(featureFlags.SIDEBAR_REMOVE_QUICK_ACTIONS),
          'sidebar-create'
        )
        .add('sidebar-edit'),
    [sidebarPinnedMenuItems, createMenuItems]
  );
  const {openItemIds} = useMenuItemsState();
  const focusableMenuItems = useMemo(
    () =>
      flatten(
        menuItemsWithPins.map((menuItem) => [
          menuItem.id,
          ...(openItemIds[menuItem.id] && isNotNil(menuItem.content)
            ? menuItem.content.map((contentItem) => contentItem.id)
            : []),
        ])
      ),
    [menuItemsWithPins, openItemIds]
  );
  const focusableCreateMenuItems = useMemo(
    () => createMenuItems.map((menuItem) => menuItem.id),
    [createMenuItems]
  );

  const setSidebarItemRef = (ref: HTMLElement | null, id: string) => {
    const index = focusableSidebarItems.indexOf(id);
    if (index !== -1) {
      assignSafely(sidebarLinkRefs.current, index, ref);
    }
  };

  const setMenuItemRef = (ref: HTMLElement | null, id: string) => {
    const index = focusableMenuItems.indexOf(id);
    if (index !== -1) {
      assignSafely(menuRefs.current, index, ref);
    }
  };

  const setCreateMenuItemRef = (ref: HTMLElement | null, id: string) => {
    const index = focusableCreateMenuItems.indexOf(id);
    if (index !== -1) {
      assignSafely(createMenuRefs.current, index, ref);
    }
  };

  const openMenu = () => {
    setFocusedMenuItemIndex(0);
  };

  const closeMenu = () => {
    setFocusedMenuItemIndex(null);
  };

  const openCreateMenu = () => {
    setFocusedCreateMenuItemIndex(0);
  };

  const closeCreateMenu = () => {
    setFocusedCreateMenuItemIndex(null);
  };

  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    const isInMenu = isNotNil(focusedMenuItemIndex);
    const isInCreateMenu = isNotNil(focusedCreateMenuItemIndex);
    const index = match([isInMenu, isInCreateMenu])
      .with([true, false], () => focusedMenuItemIndex)
      .with([false, true], () => focusedCreateMenuItemIndex)
      .with([false, false], () => focusedSidebarItemIndex)
      .otherwise(() => {
        throw new Error('Invalid state');
      });
    const lastIndex = match([isInMenu, isInCreateMenu])
      .with([true, false], () => focusableMenuItems.length - 1)
      .with([false, true], () => focusableCreateMenuItems.length - 1)
      .with([false, false], () => focusableSidebarItems.length - 1)
      .otherwise(() => {
        throw new Error('Invalid state');
      });
    const setIndex = match([isInMenu, isInCreateMenu])
      .with([true, false], () => setFocusedMenuItemIndex)
      .with([false, true], () => setFocusedCreateMenuItemIndex)
      .with([false, false], () => setFocusedSidebarItemIndex)
      .otherwise(() => {
        throw new Error('Invalid state');
      });

    if (event.key === 'ArrowDown') {
      if (isNotNil(index) && index < lastIndex) {
        setIndex(index + 1);
      }
    } else if (event.key === 'ArrowUp') {
      if (isNotNil(index) && index > 0) {
        setIndex(index - 1);
      }
    } else if (event.key === 'Home') {
      setIndex(0);
    } else if (event.key === 'End') {
      setIndex(lastIndex);
    } else {
      return; // Other keys have default behavior and propagate to the parent element
    }

    event.stopPropagation();
    event.preventDefault();
  };

  const updateFocus = useCallback(() => {
    const isInMenu = isNotNil(focusedMenuItemIndex);
    const isInCreateMenu = isNotNil(focusedCreateMenuItemIndex);
    const isInSidebar = isNotNil(focusedSidebarItemIndex);

    if (isInMenu) {
      const menuRef = getSafely(menuRefs.current, focusedMenuItemIndex);
      if (menuRef) {
        menuRef.focus();
      }
    } else if (isInCreateMenu) {
      const createMenuRef = getSafely(createMenuRefs.current, focusedCreateMenuItemIndex);
      if (createMenuRef) {
        createMenuRef.focus();
      }
    } else if (isInSidebar) {
      const linkRef = getSafely(sidebarLinkRefs.current, focusedSidebarItemIndex);
      if (linkRef) {
        linkRef.focus();
      }
    }
  }, [focusedSidebarItemIndex, focusedMenuItemIndex, focusedCreateMenuItemIndex]);

  useEffect(() => {
    updateFocus();
  }, [updateFocus]);

  useEffect(() => {
    const sidebarElement = sidebarRef.current;
    if (!sidebarElement) {
      return;
    }

    const handleFocus = () => {
      updateFocus();
    };

    sidebarElement.addEventListener('focus', handleFocus);

    return () => {
      sidebarElement.removeEventListener('focus', handleFocus);
    };
  }, [updateFocus]);

  return (
    <SidebarKeyboardControlContext.Provider
      value={{
        setSidebarItemRef,
        setMenuItemRef,
        setCreateMenuItemRef,
        openMenu,
        closeMenu,
        openCreateMenu,
        closeCreateMenu,
      }}
    >
      {children(sidebarRef, onKeyDown)}
    </SidebarKeyboardControlContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSidebarKeyboardControl = () => {
  const context = useContext(SidebarKeyboardControlContext);
  if (!context) {
    throw new Error(
      'useSidebarKeyboardControl must be used within a SidebarKeyboardControlProvider'
    );
  }
  return context;
};
